import React, { useMemo, useEffect, useRef, useCallback, memo } from 'react';
import { generateTimelineColumns } from '../utils';

// Virtualization threshold - only virtualize when we have many columns
const VIRTUALIZATION_THRESHOLD = 150;
// Buffer columns on each side of visible area
const BUFFER_COLUMNS = 30;

/**
 * TimelineHeader Component
 * 
 * Two-tier timeline header:
 * - Top row: Grouping context (months, years, etc.) - labels stick to left edge
 * - Bottom row: Primary time units (days, weeks, months, etc.)
 * 
 * Uses DOM-based virtualization for performance at high zoom levels.
 * Memoized to prevent re-renders during zoom animation.
 */
const TimelineHeader = memo(({ startDate, endDate, totalDays, zoomLevel = 'day', todayPosition, cursorPosition, timelineMinWidth = 800 }) => {
  const { columns, groups } = useMemo(() => {
    return generateTimelineColumns(startDate, endDate, totalDays, zoomLevel);
  }, [startDate, endDate, totalDays, zoomLevel]);

  const hasGroups = groups && groups.length > 0;
  const timelineRef = useRef(null);
  const columnsContainerRef = useRef(null);
  const groupLabelsRef = useRef([]);
  const visibleRangeRef = useRef({ start: 0, end: Math.min(columns.length, BUFFER_COLUMNS * 2) });
  const columnElementsRef = useRef(new Map()); // Pool of column elements
  
  const shouldVirtualize = columns.length > VIRTUALIZATION_THRESHOLD;

  // Update column visibility directly via DOM (no React re-render)
  const updateColumnVisibility = useCallback(() => {
    if (!timelineRef.current || !shouldVirtualize || !columnsContainerRef.current) return;
    
    const chartEl = timelineRef.current.closest('.gantt-view__chart');
    if (!chartEl) return;
    
    const scrollLeft = chartEl.scrollLeft;
    const viewportWidth = chartEl.clientWidth;
    const timelineWidth = timelineRef.current.offsetWidth;
    
    if (timelineWidth === 0) return;
    
    // Calculate visible percentage range
    const visibleStartPct = (scrollLeft / timelineWidth) * 100;
    const visibleEndPct = ((scrollLeft + viewportWidth) / timelineWidth) * 100;
    
    // Find column indices that fall within visible range (with buffer)
    const columnWidth = 100 / columns.length;
    const bufferPct = BUFFER_COLUMNS * columnWidth;
    
    const startPct = Math.max(0, visibleStartPct - bufferPct);
    const endPct = Math.min(100, visibleEndPct + bufferPct);
    
    const newStart = Math.max(0, Math.floor((startPct / 100) * columns.length));
    const newEnd = Math.min(columns.length, Math.ceil((endPct / 100) * columns.length));
    
    const prevRange = visibleRangeRef.current;
    
    // Only update if changed significantly
    if (Math.abs(prevRange.start - newStart) < 10 && Math.abs(prevRange.end - newEnd) < 10) {
      return;
    }
    
    visibleRangeRef.current = { start: newStart, end: newEnd };
    
    // Hide columns that went out of view
    columnElementsRef.current.forEach((el, idx) => {
      if (idx < newStart || idx >= newEnd) {
        el.style.display = 'none';
      }
    });
    
    // Show/create columns that came into view
    const container = columnsContainerRef.current;
    for (let i = newStart; i < newEnd; i++) {
      let el = columnElementsRef.current.get(i);
      if (el) {
        el.style.display = '';
      } else {
        // Create new column element
        const column = columns[i];
        el = document.createElement('div');
        el.className = `gantt-timeline-header__column${column.isWeekend ? ' gantt-timeline-header__column--weekend' : ''}${column.isFirstOfMonth ? ' gantt-timeline-header__column--first-of-month' : ''}${column.isCurrent ? ' gantt-timeline-header__column--current' : ''}`;
        el.style.left = `${column.position}%`;
        el.style.width = `${column.width}%`;
        el.innerHTML = `<span class="gantt-timeline-header__date">${column.label}</span>`;
        container.appendChild(el);
        columnElementsRef.current.set(i, el);
      }
    }
  }, [columns, shouldVirtualize]);

  // Direct DOM updates for sticky labels
  const updateLabelPositions = useCallback(() => {
    if (!timelineRef.current || !hasGroups) return;
    
    const chartEl = timelineRef.current.closest('.gantt-view__chart');
    if (!chartEl) return;
    
    const scrollLeft = chartEl.scrollLeft;
    const timelineWidth = timelineRef.current.offsetWidth;
    
    groups.forEach((group, index) => {
      const labelEl = groupLabelsRef.current[index];
      if (!labelEl) return;
      
      const groupLeftPx = (group.position / 100) * timelineWidth;
      const groupWidthPx = (group.width / 100) * timelineWidth;
      
      const scrollOffset = scrollLeft - groupLeftPx;
      
      let offset = 0;
      if (scrollOffset > 0) {
        const maxOffset = groupWidthPx - 80;
        offset = Math.min(scrollOffset, Math.max(0, maxOffset));
      }
      
      labelEl.style.transform = `translateX(${offset}px)`;
    });
  }, [groups, hasGroups]);

  // Combined scroll handler
  const handleScroll = useCallback(() => {
    updateLabelPositions();
    if (shouldVirtualize) {
      updateColumnVisibility();
    }
  }, [updateLabelPositions, updateColumnVisibility, shouldVirtualize]);

  // Listen for scroll events
  useEffect(() => {
    const chartEl = timelineRef.current?.closest('.gantt-view__chart');
    if (!chartEl) return;
    
    chartEl.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => chartEl.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Reset virtualization when columns change or virtualization mode changes
  useEffect(() => {
    if (!columnsContainerRef.current) return;
    
    // Always clear DOM-created elements when columns change
    // (React will re-render its own elements automatically)
    columnElementsRef.current.forEach(el => el.remove());
    columnElementsRef.current.clear();
    visibleRangeRef.current = { start: 0, end: 0 };
    
    // If virtualizing, trigger initial render
    if (shouldVirtualize) {
      requestAnimationFrame(updateColumnVisibility);
    }
  }, [columns, shouldVirtualize, updateColumnVisibility]);

  // Store refs for group labels
  const setLabelRef = useCallback((el, index) => {
    groupLabelsRef.current[index] = el;
  }, []);

  return (
    <div className={`gantt-timeline-header ${hasGroups ? 'gantt-timeline-header--two-tier' : ''}`}>
      {/* Left sidebar (task names column) - sticky */}
      <div className="gantt-timeline-header__sidebar">
        <span>Mission</span>
      </div>

      {/* Timeline area */}
      <div 
        className="gantt-timeline-header__timeline" 
        style={{ minWidth: timelineMinWidth }}
        ref={timelineRef}
      >
        {/* Secondary row - groupings (months, years, etc.) */}
        {hasGroups && (
          <div className="gantt-timeline-header__groups">
            {groups.map((group, index) => (
              <div
                key={`${group.label}-${index}`}
                className="gantt-timeline-header__group"
                style={{
                  left: `${group.position}%`,
                  width: `${group.width}%`,
                }}
              >
                <span 
                  className="gantt-timeline-header__group-label"
                  ref={(el) => setLabelRef(el, index)}
                >
                  {group.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Primary row - time units (virtualized when needed) */}
        <div className="gantt-timeline-header__columns" ref={columnsContainerRef}>
          {/* Non-virtualized: render all columns via React */}
          {!shouldVirtualize && columns.map((column, index) => (
            <div
              key={index}
              className={`gantt-timeline-header__column${column.isWeekend ? ' gantt-timeline-header__column--weekend' : ''}${column.isFirstOfMonth ? ' gantt-timeline-header__column--first-of-month' : ''}${column.isCurrent ? ' gantt-timeline-header__column--current' : ''}`}
              style={{
                left: `${column.position}%`,
                width: `${column.width}%`,
              }}
            >
              <span className="gantt-timeline-header__date">{column.label}</span>
            </div>
          ))}
          {/* Virtualized columns are managed via DOM directly */}
        </div>

        {/* Today marker - just a colored line, no text */}
        {todayPosition !== null && (
          <div 
            className="gantt-today-marker gantt-today-marker--header"
            style={{ left: `${todayPosition}%` }}
          />
        )}

        {/* Cursor/playhead marker (DAW-style) */}
        {cursorPosition !== null && (
          <div 
            className="gantt-cursor-marker gantt-cursor-marker--header"
            style={{ left: `${cursorPosition}%` }}
          />
        )}
      </div>
    </div>
  );
});

TimelineHeader.displayName = 'TimelineHeader';

export default TimelineHeader;
